import { createContext, useContext, useEffect, useState } from 'react';

const KioskContext = createContext({ isKiosk: false, kioskDisplay: null });

const KIOSK_STORAGE_KEY = 'bluppimart_kiosk_mode';
const KIOSK_DISPLAY_STORAGE_KEY = 'bluppimart_kiosk_display';

// Hardware profiles selectable with ?display=…; null = the default 52"
// 1080×1920 portrait screen. ?display=default clears a stored profile.
export const KIOSK_DISPLAYS = ['ipad'];

const detectInitialKioskMode = () => {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  if (params.get('kiosk') === '1') return true;
  if (params.get('kiosk') === '0') return false;
  if (process.env.REACT_APP_KIOSK_MODE === 'true') return true;
  return window.sessionStorage.getItem(KIOSK_STORAGE_KEY) === '1';
};

const detectInitialKioskDisplay = () => {
  if (typeof window === 'undefined') return null;
  const param = new URLSearchParams(window.location.search).get('display');
  if (KIOSK_DISPLAYS.includes(param)) return param;
  if (param === 'default') return null;
  const stored = window.sessionStorage.getItem(KIOSK_DISPLAY_STORAGE_KEY);
  return KIOSK_DISPLAYS.includes(stored) ? stored : null;
};

export const KioskProvider = ({ children }) => {
  const [isKiosk, setIsKiosk] = useState(detectInitialKioskMode);
  const [kioskDisplay] = useState(detectInitialKioskDisplay);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (isKiosk) {
      window.sessionStorage.setItem(KIOSK_STORAGE_KEY, '1');
      document.documentElement.classList.add('kiosk-mode-root');
    } else {
      window.sessionStorage.removeItem(KIOSK_STORAGE_KEY);
      document.documentElement.classList.remove('kiosk-mode-root');
    }
  }, [isKiosk]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (kioskDisplay) window.sessionStorage.setItem(KIOSK_DISPLAY_STORAGE_KEY, kioskDisplay);
    else window.sessionStorage.removeItem(KIOSK_DISPLAY_STORAGE_KEY);
  }, [kioskDisplay]);

  // Root-level class so a profile can change the rem base (kiosk.css) — the
  // site sizes almost all type in rem, so that scales every font at once.
  useEffect(() => {
    if (typeof window === 'undefined' || !isKiosk || !kioskDisplay) return undefined;
    const cls = `kiosk-display-${kioskDisplay}-root`;
    document.documentElement.classList.add(cls);
    return () => document.documentElement.classList.remove(cls);
  }, [isKiosk, kioskDisplay]);

  return (
    <KioskContext.Provider value={{ isKiosk, setIsKiosk, kioskDisplay: isKiosk ? kioskDisplay : null }}>
      {children}
    </KioskContext.Provider>
  );
};

export const useKiosk = () => useContext(KioskContext);
